import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://api.businessup.com.tr/api'
});

// İstek interceptor'ı - localStorage'dan token veya embedToken ekle
api.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token');
    const embedToken = localStorage.getItem('embedToken');
    
    if (!config.headers.Authorization) {
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      } else if (embedToken) {
        config.headers.Authorization = `Bearer ${embedToken}`;
      }
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Hata yakalama interceptor'ı
api.interceptors.response.use(
  response => response,
  error => {
    console.error('API Hatası:', {
      url: error.config?.url,
      method: error.config?.method,
      data: error.config?.data,
      status: error.response?.status,
      statusText: error.response?.statusText,
      responseData: error.response?.data,
      message: error.message
    });
    return Promise.reject(error);
  }
);

// Aylık Metrik İşlemleri
const monthlyMetricService = {
  getMonthlyMetrics: async (brandId) => {
    const response = await api.get('/monthly-metrics');
    return response.data.filter(metric => 
      metric.Brands.some(brand => brand.id === brandId)
    );
  },

  getMonthlyMetricValues: async (brandId, date) => {
    try {
      const response = await api.get(`/monthly-metrics/brands/${brandId}`, {
        params: { date }
      });
      console.log('API yanıtı:', response.data);
      
      // InternalNotes alanını kontrol et
      const hasInternalNotes = response.data.some(metric => metric.internalNotes !== undefined);
      console.log('API yanıtında internalNotes var mı?', hasInternalNotes);
      console.log('Örnek metrik:', response.data[0]);
      
      return response.data.map(metric => {
        // Para birimi için kuruşları kaldır
        let formattedValue = metric.value;
        if (metric.dataType === 'currency' && formattedValue !== null && formattedValue !== undefined) {
          // String veya sayı tipinde olabilir, parseFloat ile sayıya çevir
          const numValue = parseFloat(formattedValue);
          if (!isNaN(numValue)) {
            // Kuruşları kaldır (tam sayıya yuvarla)
            formattedValue = Math.round(numValue).toString();
          }
        }
        
        return {
          id: metric.id,
          name: metric.name,
          description: metric.description,
          value: formattedValue,
          isAutoCalculated: metric.isAutoCalculated,
          formula: metric.formula,
          isVisible: metric.isVisible,
          displayOrder: metric.displayOrder || 0,
          color: metric.color,
          dataType: metric.dataType,
          internalNotes: metric.internalNotes
        };
      });
    } catch (error) {
      console.error('API hatası:', error);
      throw error;
    }
  },

  saveMonthlyMetricValue: async ({ brandId, monthlyMetricId, date, value, isManualOverride }) => {
    const response = await api.post(`/monthly-metrics/${monthlyMetricId}/brands/${brandId}/data`, {
      value,
      date,
      isManualOverride
    });
    return response.data;
  },

  updateMonthlyMetricVisibility: async ({ brandId, monthlyMetricId, isVisible }) => {
    try {
      const response = await api.patch(`/monthly-metrics/${monthlyMetricId}/brands/${brandId}/visibility`, {
        isVisible
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Aylık metrik görünürlük güncelleme hatası:', error);
      return { success: false, error: error.response?.data?.error || 'Bir hata oluştu' };
    }
  },

  recalculateMonthlyMetrics: async (brandId, date) => {
    try {
      // Tarihi YYYY-MM formatına çevir
      const formattedDate = date.substring(0, 7); // "2024-03-15" -> "2024-03"
      
      const response = await api.post(`/monthly-metrics/brands/${brandId}/recalculate`, null, {
        params: { date: formattedDate }
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Aylık metrik yeniden hesaplama hatası:', error);
      return { success: false, error: error.response?.data?.error || 'Bir hata oluştu' };
    }
  },

  updateMonthlyMetricDisplayOrder: async (brandId, metrics) => {
    try {
      const response = await api.patch(`/monthly-metrics/brands/${brandId}/display-order`, {
        metrics
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Aylık metrik sıralama düzeni güncelleme hatası:', error);
      return { success: false, error: error.response?.data?.error || 'Bir hata oluştu' };
    }
  },

  updateAllBrandsMetricDisplayOrder: async (metrics, brandIds, isSummaryUpdate = false) => {
    try {
      const response = await api.patch('/monthly-metrics/display-order/all-brands', {
        metrics: metrics.map(metric => ({
          id: metric.id,
          displayOrder: metric.displayOrder,
          isVisible: metric.isVisible
        })),
        brandIds: brandIds, // Seçili markaların ID'lerini gönder
        isSummaryUpdate: isSummaryUpdate // Özet görünüm için mi?
      });
      return { success: true, data: response.data, brandCount: response.data.brandCount };
    } catch (error) {
      console.error('Metrik sıralama düzeni güncelleme hatası:', error);
      return { success: false, error: error.response?.data?.error || 'Bir hata oluştu' };
    }
  },

  // Tüm aktif markaların aylık metriklerini tek seferde getir
  getAllActiveBrandsMonthlyMetrics: async (date) => {
    try {
      const response = await api.get('/monthly-metrics/all-active-brands', {
        params: { date }
      });
      
      // API'den gelen veri formatı: { brandId: { brandName, metrics: [...] } }
      const result = {};
      
      Object.entries(response.data).forEach(([brandId, brandData]) => {
        result[brandId] = {
          brandName: brandData.brandName,
          metrics: brandData.metrics.map(metric => {
            // Para birimi için kuruşları kaldır
            let formattedValue = metric.value;
            if (metric.dataType === 'currency' && formattedValue !== null && formattedValue !== undefined) {
              const numValue = parseFloat(formattedValue);
              if (!isNaN(numValue)) {
                formattedValue = Math.round(numValue).toString();
              }
            }
            
            return {
              id: metric.id,
              name: metric.name,
              description: metric.description,
              value: formattedValue,
              isAutoCalculated: metric.isAutoCalculated,
              formula: metric.formula,
              isVisible: metric.isVisible,
              displayOrder: metric.displayOrder || 0,
              dataType: metric.dataType,
              internalNotes: metric.internalNotes
            };
          })
        };
      });
      
      return result;
    } catch (error) {
      console.error('Tüm aktif markaların aylık metrik verileri getirme hatası:', error);
      throw error;
    }
  },

  // Aylık Metrik Özet (Summary) Fonksiyonları
  getMonthlyMetricSummary: async (brandId, includeHidden = false) => {
    try {
      const response = await api.get(`/monthly-metrics/brands/${brandId}/summary`, {
        params: { includeHidden: includeHidden ? 'true' : 'false' }
      });
      
      // API'den gelen metrikler
      const metrics = response.data.map(metric => {
        // Para birimi için kuruşları kaldır
        const values = metric.values.map(val => {
          let formattedValue = val.value;
          if (metric.dataType === 'currency' && formattedValue !== null && formattedValue !== undefined) {
            const numValue = parseFloat(formattedValue);
            if (!isNaN(numValue)) {
              formattedValue = Math.round(numValue).toString();
            }
          }
          return {
            ...val,
            value: formattedValue
          };
        });
        
        return {
          id: metric.id,
          name: metric.name,
          description: metric.description,
          values: values,
          isAutoCalculated: metric.isAutoCalculated,
          formula: metric.formula,
          isVisible: metric.isSummaryVisible,
          displayOrder: metric.summaryDisplayOrder || 0,
          // Orijinal sıralama değerlerini de sakla
          summaryDisplayOrder: metric.summaryDisplayOrder || 0, 
          isSummaryVisible: metric.isSummaryVisible,
          color: metric.color,
          dataType: metric.dataType,
          internalNotes: metric.internalNotes
        };
      });
      
      // Metrikler summaryDisplayOrder'a göre sıralanmış
      const sortedMetrics = [...metrics].sort((a, b) => {
        if (a.summaryDisplayOrder === b.summaryDisplayOrder) {
          return a.name.localeCompare(b.name);
        }
        return a.summaryDisplayOrder - b.summaryDisplayOrder;
      });
      
      return sortedMetrics;
    } catch (error) {
      console.error('Aylık metrik özeti alma hatası:', error);
      throw error;
    }
  },

  updateSummaryMetricDisplayOrder: async (brandId, metrics) => {
    try {
      const response = await api.patch(`/monthly-metrics/brands/${brandId}/summary/display-order`, {
        metrics: metrics.map(metric => ({
          id: metric.id,
          displayOrder: metric.displayOrder,
          isVisible: metric.isVisible
        }))
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Aylık özet metrik sıralama düzeni güncelleme hatası:', error);
      return { success: false, error: error.response?.data?.error || 'Bir hata oluştu' };
    }
  }
};

// Audit API methods
const auditAPI = {
  // Audit Settings
  getAuditSettings: () => api.get('/audit-settings'),
  updateAuditSettings: (data) => api.put('/audit-settings', data),
  
  // Brand Audits
  triggerBrandAudit: (brandId, auditData) => api.post(`/audits/brands/${brandId}/audit`, auditData, { timeout: 600000 }), // 10 minutes timeout
  getBrandAudits: (brandId, params = {}) => api.get(`/audits/brands/${brandId}/audits`, { params }),
  getBrandAuditCapabilities: (brandId) => api.get(`/audits/brands/${brandId}/capabilities`),
  getAuditById: (auditId) => api.get(`/audits/${auditId}`),
  getAllAudits: (params = {}) => api.get('/audits', { params }),
  
  // Bulk Audit - Fire-and-forget
  bulkAuditAllBrands: () => api.post('/audits/bulk-audit', {}, { timeout: 10000 }), // 10 seconds timeout (immediate response)
  
  // Cleanup functions
  cleanupStuckAudits: (timeoutMinutes = 10) => api.post('/audits/cleanup-stuck', { timeoutMinutes }),
  cancelPendingAudit: (auditId) => api.post(`/audits/${auditId}/cancel`),
  deleteAudit: (auditId) => api.delete(`/audits/${auditId}`),
  toggleAuditVisibility: (auditId) => api.patch(`/audits/${auditId}/toggle-visibility`)
};

// Saatlik Veri İşlemleri
const hourlyDataService = {
  // Saatlik verileri getir
  getHourlyData: async (brandId, startDate, endDate) => {
    const params = {};
    if (brandId) params.brandId = brandId;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const response = await api.get('/hourly-data', { params });
    return response.data;
  },

  // Tek saatlik veri oluştur/güncelle
  createHourlyData: async (brandId, date, data) => {
    const response = await api.post('/hourly-data', {
      brandId,
      date,
      data
    });
    return response.data;
  },

  // Toplu saatlik veri oluştur/güncelle
  createBatchHourlyData: async (entries) => {
    const response = await api.post('/hourly-data/batch', {
      entries
    });
    return response.data;
  },

  // Saatlik veriyi sil
  deleteHourlyData: async (id) => {
    const response = await api.delete(`/hourly-data/${id}`);
    return response.data;
  },

  // Tarih aralığı için toplu veri getir
  getHourlyDataRange: async (brandId, startDate, endDate, aggregation = 'hour') => {
    const params = {
      brandId,
      startDate,
      endDate,
      aggregation
    };

    const response = await api.get('/hourly-data/range', { params });
    return response.data;
  }
};

export { api as default, monthlyMetricService, auditAPI, hourlyDataService }; 